import { useUser } from "@clerk/tanstack-react-start";
import { useForm, useStore } from "@tanstack/react-form";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  addDays,
  addYears,
  getDay,
  isAfter,
  isBefore,
  startOfDay,
} from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { ArrowLeft, CalendarIcon, Info, Lock, Plus } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import type { ProgramTemplateSelect } from "~/../drizzle/schema";
import { ClientSelector, buildFullName } from "~/components/client-selector";
import {
  ProgramTemplatesDialog,
  type ProgramTemplateDraft,
} from "~/components/engagement/program-templates-dialog";
import { Button } from "~/components/ui/button";
import { Calendar } from "~/components/ui/calendar";
import { Card } from "~/components/ui/card";
import { Checkbox } from "~/components/ui/checkbox";
import { ErrorAlert } from "~/components/ui/error-alert";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { PageHeader } from "~/components/ui/page-title";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import { useCoachPrivateProfileRequired } from "~/contexts/coach";
import { useEngagementSubmission } from "~/hooks/use-engagement-submission";
import { useScheduleBuilder } from "~/hooks/use-schedule-builder";
import {
  useCoachClientRelationships,
  useCreateEngagement,
} from "~/lib/queries/engagementQuery";
import { programTemplatesQueryOptions } from "~/lib/queries/programTemplateQuery";
import { useCoachSessionsInRange } from "~/lib/queries/sessionQuery";
import type { CreateEngagementInput } from "~/lib/schemas/engagement";
import { formatCadenceSummary } from "~/lib/shared/program-summary";
import {
  formatDateOnly,
  getClientTimezone,
  getCurrentTimeInClientTimezone,
} from "~/lib/utils/timezone";

import {
  AddClientDialog,
  type NewClient,
} from "./-components/add-client-dialog";
import {
  InvoiceScheduleEditor,
  type InvoiceDraft,
} from "./-components/invoice-schedule-editor";
import { NewClientCard } from "./-components/new-client-card";
import { ProgramScheduleSection } from "./-components/program-schedule-section";
import { programStepBlocker } from "./-components/program-step-blocker";
import { ProgramSummaryRail } from "./-components/program-summary-rail";
import { ProgramTemplatePicker } from "./-components/program-template-picker";
import {
  programFromTemplate,
  templateDraftFromProgram,
} from "./-components/program-template-values";
import { SectionCard } from "./-components/section-card";

export const Route = createFileRoute("/_user/my-coaching/clients/programs/new")(
  {
    component: NewProgramPage,
    staticData: { title: "New program" },
  },
);

/** A past client picked from the list, or someone the coach added by hand. */
type ProgramClient =
  | {
      kind: "existing";
      id: string;
      name: string;
      email?: string;
      avatar?: string;
    }
  | ({ kind: "new" } & NewClient);

const firstNameOf = (client: ProgramClient): string =>
  client.kind === "new" ? client.firstName : client.name.split(" ")[0];

interface NewProgramFormValues {
  selectedClient?: ProgramClient;
  startDate?: Date;
  endDate?: Date;
  meetingLink: string;
  askInitialGoals: boolean;
  programType: "free" | "paid";
  invoices: InvoiceDraft[];
  /** The template last picked; the program gets its homework. */
  template?: Pick<ProgramTemplateSelect, "id" | "name" | "homework">;
}

function NewProgramPage() {
  const coach = useCoachPrivateProfileRequired();
  const canCreate = coach.stripeChargesEnabled && coach.contractSigned;

  if (!canCreate) {
    return (
      <div className="mx-auto max-w-2xl py-12">
        <Card className="items-start gap-3 border-amber-200 bg-amber-50 p-6">
          <div className="flex items-center gap-3">
            <Lock className="size-5 shrink-0 text-amber-600" />
            <div>
              <p className="font-medium">Finish your setup first</p>
              <p className="text-text-secondary text-sm">
                Connect Stripe and sign your coach agreement before creating
                coaching programs.
              </p>
            </div>
          </div>
          <Link to="/my-coaching/clients/programs" className="mt-2">
            <Button variant="default" size="sm" className="gap-2">
              <ArrowLeft className="size-4" />
              Back to client programs
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  return <NewProgramForm />;
}

function NewProgramForm() {
  const navigate = useNavigate();
  const coachTimeZone = getClientTimezone();
  const coach = useCoachPrivateProfileRequired();
  const { user } = useUser();

  const initialValues = useMemo<NewProgramFormValues>(() => {
    const now = getCurrentTimeInClientTimezone();
    return {
      selectedClient: undefined,
      startDate: now,
      endDate: addDays(now, 7),
      meetingLink: "",
      askInitialGoals: false,
      programType: "paid",
      invoices: [{ invoiceDate: undefined, invoiceAmount: "" }],
    };
  }, []);

  const form = useForm({ defaultValues: initialValues });
  const values = useStore(form.store, (s) => s.values);

  // ── Client search ────────────────────────────────────────────────────────
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchValue), 400);
    return () => clearTimeout(timer);
  }, [searchValue]);

  const { data: searchClients, isLoading: isLoadingClients } =
    useCoachClientRelationships(debouncedSearch || undefined);

  const clientOptions = useMemo(
    () =>
      (searchClients ?? []).map((c) => ({
        id: c.id,
        name: buildFullName(c),
        firstName: c.firstName,
        lastName: c.lastName,
        email: c.email,
        avatar: undefined,
      })),
    [searchClients],
  );

  // ── Adding a new client ──────────────────────────────────────────────────
  const ownEmails = useMemo(
    () =>
      [
        coach.email,
        ...(user?.emailAddresses.map((address) => address.emailAddress) ?? []),
      ].filter((email): email is string => !!email),
    [coach.email, user],
  );
  // Closed: null. Open: the values the form starts with.
  const [addClientInitial, setAddClientInitial] =
    useState<Partial<NewClient> | null>(null);

  // ── Schedule builder ─────────────────────────────────────────────────────
  const { data: rangeSessions } = useCoachSessionsInRange(
    values.startDate,
    values.endDate,
  );

  const existingSessions = useMemo(
    () =>
      (rangeSessions ?? []).map((s) => ({
        startAt: s.startAt,
        duration: s.duration,
        title: s.title,
      })),
    [rangeSessions],
  );

  const builder = useScheduleBuilder({
    startDate: values.startDate,
    endDate: values.endDate,
    existingSessions,
    coachTimeZone,
  });

  // Keep the rhythm's weekday aligned with the start date until the coach picks
  // their own weekday(s) or hand-edits the generated list.
  const weekdaysTouchedRef = useRef(false);
  const handleRhythmChange = (
    patch: Parameters<typeof builder.updateRhythm>[0],
  ) => {
    if (patch.weekdays) weekdaysTouchedRef.current = true;
    builder.updateRhythm(patch);
  };
  const startDay = values.startDate
    ? getDay(startOfDay(values.startDate))
    : null;
  useEffect(() => {
    if (
      startDay === null ||
      weekdaysTouchedRef.current ||
      builder.isCustomized
    ) {
      return;
    }
    builder.updateRhythm({ weekdays: [startDay] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDay]);

  // ── Program templates ────────────────────────────────────────────────────
  const { data: templates, isLoading: isLoadingTemplates } = useQuery(
    programTemplatesQueryOptions(),
  );

  const applyTemplate = (template: ProgramTemplateSelect) => {
    const start =
      form.state.values.startDate ?? getCurrentTimeInClientTimezone();
    const program = programFromTemplate(template, start);
    form.setFieldValue("endDate", program.endDate);
    // Template days win over the start date's weekday; a template without
    // them hands the weekday back to the start date.
    weekdaysTouchedRef.current = program.weekdays !== null;
    builder.updateRhythm({
      ...program.rhythm,
      weekdays: program.weekdays ?? [getDay(startOfDay(start))],
      ...(program.time ? { time: program.time } : {}),
    });
    builder.setSessionTitles(program.sessionTitles);
    form.setFieldValue("meetingLink", program.meetingLink);
    form.setFieldValue("askInitialGoals", program.askInitialGoals);
    form.setFieldValue("programType", program.programType);
    form.setFieldValue("invoices", program.invoices);
    form.setFieldValue("template", template);
  };

  const clearTemplate = useCallback(
    () => form.setFieldValue("template", undefined),
    [form],
  );

  // Closed: null. Open on the template list: {}. Open on a new template:
  // { newTemplate }, prefilled with those values.
  const [templateDialog, setTemplateDialog] = useState<{
    newTemplate?: ProgramTemplateDraft;
  } | null>(null);
  const saveAsTemplate = () => {
    const v = form.state.values;
    setTemplateDialog({
      newTemplate: templateDraftFromProgram({
        startDate: v.startDate,
        sessions: builder.drafts,
        rhythm: builder.rhythm,
        isCustomized: builder.isCustomized,
        meetingLink: v.meetingLink,
        askInitialGoals: v.askInitialGoals,
        programType: v.programType,
        invoices: v.invoices,
      }),
    });
  };

  // ── Invoice array helpers (TanStack Form) ────────────────────────────────
  const handleProgramTypeChange = (type: "free" | "paid") => {
    form.setFieldValue("programType", type);
    if (type === "free") {
      form.setFieldValue("invoices", []);
    } else if (form.state.values.invoices.length === 0) {
      form.setFieldValue("invoices", [
        { invoiceDate: undefined, invoiceAmount: "" },
      ]);
    }
  };
  const handleAddInvoice = () => {
    form.setFieldValue("invoices", [
      ...form.state.values.invoices,
      { invoiceDate: undefined, invoiceAmount: "" },
    ]);
  };
  const handleRemoveInvoice = (index: number) => {
    form.setFieldValue(
      "invoices",
      form.state.values.invoices.filter((_, i) => i !== index),
    );
  };
  const handleInvoiceChange = (index: number, patch: Partial<InvoiceDraft>) => {
    form.setFieldValue(
      "invoices",
      form.state.values.invoices.map((inv, i) =>
        i === index ? { ...inv, ...patch } : inv,
      ),
    );
  };

  // ── Submission ───────────────────────────────────────────────────────────
  const createEngagement = useCreateEngagement();

  const { submitError, isSubmitting, submitEngagement, clearError } =
    useEngagementSubmission({
      onSubmit: async (data: CreateEngagementInput) => {
        const result = await createEngagement.mutateAsync({ data });
        const client = form.state.values.selectedClient;
        if (!result.invitationSent) {
          toast.warning(
            `Coaching program created, but we couldn't email ${client ? firstNameOf(client) : "your client"}. Check the address and contact them directly.`,
          );
        } else {
          // Same words whether the address already had an account or not, so
          // the page never reveals which.
          toast.success(
            client?.kind === "new"
              ? `Coaching program created. We emailed ${client.firstName} at ${client.email}.`
              : "Coaching program created",
          );
        }
        await navigate({
          to: "/my-coaching/clients/programs/$engagementId",
          params: { engagementId: result.engagementId },
        });
      },
    });

  const errorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (submitError && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [submitError]);

  const handleSubmit = () => {
    const v = form.state.values;
    if (!v.selectedClient || !v.startDate || !v.endDate) return;
    const paidInvoices =
      v.programType === "paid"
        ? v.invoices.filter(
            (inv): inv is { invoiceDate: Date; invoiceAmount: string } =>
              inv.invoiceDate != null,
          )
        : [];
    const client = v.selectedClient;
    submitEngagement(
      {
        selectedClient:
          client.kind === "new"
            ? {
                // The submission hook sends a `temp-` client by email.
                id: "temp-new-client",
                name: `${client.firstName} ${client.lastName}`,
                email: client.email,
                firstName: client.firstName,
                lastName: client.lastName,
              }
            : client,
        startDate: v.startDate,
        endDate: v.endDate,
        meetingLink: v.meetingLink,
        programType: v.programType,
        askInitialGoals: v.askInitialGoals,
        invoices: paidInvoices,
        programTemplateId: v.template?.id,
      },
      builder.getSessionsForSubmission(),
    );
  };

  // ── Derived summary + gating ─────────────────────────────────────────────
  const sessionCount = builder.drafts.length;
  const cadenceSummary = formatCadenceSummary({
    sessionCount,
    cadence: builder.rhythm.cadence,
    weekdays: builder.rhythm.weekdays,
    isCustomized: builder.isCustomized,
  });

  const invoiceTotal =
    values.programType === "paid"
      ? values.invoices.reduce((sum, inv) => {
          const amount = Number.parseFloat(inv.invoiceAmount);
          return sum + (Number.isFinite(amount) ? amount : 0);
        }, 0)
      : undefined;

  const blocker = programStepBlocker({
    hasClient: values.selectedClient !== undefined,
    hasDates: values.startDate !== undefined && values.endDate !== undefined,
    sessionCount,
    conflictCount: builder.conflicts.size,
    programType: values.programType,
    invoices: values.invoices,
  });
  const disabledReason = blocker && `Step ${blocker.step}: ${blocker.message}`;

  const submitDisabled = isSubmitting || disabledReason !== undefined;

  const selectedClient = values.selectedClient;
  const clientFirstName = selectedClient && firstNameOf(selectedClient);
  // Only dated invoices are sent with the program.
  const invoiceCount =
    values.programType === "paid"
      ? values.invoices.filter((inv) => inv.invoiceDate).length
      : 0;

  return (
    <div className="space-y-8">
      <div className="space-y-4">
        <Link
          to="/my-coaching/clients/programs"
          className="text-text-secondary hover:text-text-primary inline-flex items-center gap-1.5 text-sm transition-colors"
        >
          <ArrowLeft className="size-4" />
          Client programs
        </Link>
        <PageHeader
          eyebrow="New program"
          title={
            clientFirstName
              ? `Set up ${clientFirstName}'s coaching program`
              : "Set up a new coaching program"
          }
          description="Pick your client, shape the session rhythm, and set how they pay. You can fine-tune everything later."
        />
      </div>

      {submitError && (
        <div ref={errorRef}>
          <ErrorAlert
            error={submitError}
            onDismiss={clearError}
            title="We couldn't create this program"
          />
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="space-y-4">
          <SectionCard
            step={1}
            title="Client"
            description="Who you're coaching."
            delay={0.05}
          >
            <form.Field name="selectedClient">
              {(field) => {
                const client = field.state.value;
                return (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <Label className="text-sm font-medium">Client</Label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="gap-1.5 text-purple-700"
                        onClick={() =>
                          setAddClientInitial(
                            client?.kind === "new" ? client : {},
                          )
                        }
                      >
                        <Plus className="size-4" />
                        Add new client
                      </Button>
                    </div>
                    {client?.kind === "new" ? (
                      <NewClientCard
                        client={client}
                        onEdit={() => setAddClientInitial(client)}
                        onRemove={() => field.handleChange(undefined)}
                      />
                    ) : (
                      <ClientSelector
                        clients={clientOptions}
                        selectedClient={client}
                        onAddNewClient={(email) =>
                          setAddClientInitial({ email })
                        }
                        onClientChange={(picked) =>
                          field.handleChange(
                            picked
                              ? {
                                  kind: "existing",
                                  id: picked.id,
                                  name: picked.name,
                                  email: picked.email ?? undefined,
                                  avatar: picked.avatar,
                                }
                              : undefined,
                          )
                        }
                        placeholder="Search by name or enter an email"
                        showBadge={false}
                        isLoading={isLoadingClients}
                        onSearchChange={setSearchValue}
                        searchValue={searchValue}
                      />
                    )}
                  </div>
                );
              }}
            </form.Field>
          </SectionCard>

          <SectionCard
            step={2}
            title="Dates and sessions"
            description="How long the program runs and how often you meet."
            delay={0.1}
          >
            <ProgramTemplatePicker
              templates={templates ?? []}
              isLoading={isLoadingTemplates}
              // Until the list has loaded, a deleted template cannot be told from a missing one.
              selectedId={templates ? values.template?.id : undefined}
              onApply={applyTemplate}
              onClear={clearTemplate}
              onManage={() => setTemplateDialog({})}
            />
            {values.template && values.template.homework.length > 0 && (
              <p className="text-text-secondary text-sm">
                {values.template.homework.length === 1
                  ? "1 homework item"
                  : `${values.template.homework.length} homework items`}{" "}
                from {values.template.name} will be scheduled. Each is added to
                your client&rsquo;s homework when its session ends.
              </p>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <form.Field name="startDate">
                {(field) => (
                  <DateField
                    label="Start date"
                    value={field.state.value}
                    placeholder="Select start date"
                    onSelect={(date) => {
                      field.handleChange(date);
                      const end = form.state.values.endDate;
                      if (!end || !isAfter(end, date)) {
                        form.setFieldValue("endDate", addDays(date, 7));
                      }
                    }}
                    isDisabled={(date) => {
                      const today = startOfDay(
                        toZonedTime(new Date(), getClientTimezone()),
                      );
                      const d = startOfDay(date);
                      return (
                        isBefore(d, today) || isAfter(d, addYears(today, 1))
                      );
                    }}
                  />
                )}
              </form.Field>

              <form.Field name="endDate">
                {(field) => (
                  <DateField
                    label="End date"
                    value={field.state.value}
                    placeholder="Select end date"
                    onSelect={(date) => field.handleChange(date)}
                    isDisabled={(date) => {
                      const start = form.state.values.startDate;
                      if (!start) return true;
                      const d = startOfDay(date);
                      return (
                        isBefore(d, startOfDay(start)) ||
                        isAfter(d, addYears(startOfDay(start), 1))
                      );
                    }}
                  />
                )}
              </form.Field>
            </div>
            <div className="border-t border-gray-100" />
            <ProgramScheduleSection
              builder={builder}
              rangeSessions={rangeSessions ?? []}
              coachTimeZone={coachTimeZone}
              startDate={values.startDate}
              endDate={values.endDate}
              onRhythmChange={handleRhythmChange}
            />
          </SectionCard>

          <SectionCard
            step={3}
            title="Payment"
            description="Free, or the invoices your client pays."
            delay={0.15}
          >
            <InvoiceScheduleEditor
              programType={values.programType}
              invoices={values.invoices}
              onProgramTypeChange={handleProgramTypeChange}
              onAddInvoice={handleAddInvoice}
              onRemoveInvoice={handleRemoveInvoice}
              onInvoiceChange={handleInvoiceChange}
              showErrors={blocker?.step === 3}
            />
          </SectionCard>

          <SectionCard
            step={4}
            optional
            title="Extras"
            description="Default meeting link and onboarding goals."
            delay={0.2}
          >
            <div className="space-y-4">
              <form.Field name="meetingLink">
                {(field) => (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">
                      Default meeting link
                    </Label>
                    <Input
                      type="url"
                      inputMode="url"
                      placeholder="https://zoom.us/j/…"
                      value={field.state.value}
                      onValueChange={field.handleChange}
                    />
                    <p className="text-text-tertiary flex items-start gap-1.5 text-xs">
                      <Info className="mt-0.5 size-3 shrink-0" />
                      <span>
                        You can add links per session later — many coaches
                        schedule each meeting in Zoom after creating the
                        program.
                      </span>
                    </p>
                  </div>
                )}
              </form.Field>

              <form.Field name="askInitialGoals">
                {(field) => (
                  <label className="text-text-secondary flex items-start gap-2 text-sm">
                    <Checkbox
                      checked={field.state.value}
                      onCheckedChange={(checked) =>
                        field.handleChange(checked === true)
                      }
                      className="mt-0.5"
                    />
                    <span>
                      Ask the client to share initial goals during onboarding
                      <span className="text-text-tertiary block text-xs">
                        They arrive as suggestions you review — nothing lands in
                        the shared goals list without you.
                      </span>
                    </span>
                  </label>
                )}
              </form.Field>
            </div>
          </SectionCard>
        </div>

        <aside className="max-lg:order-first lg:sticky lg:top-[calc(var(--app-topbar-h)+1.5rem)] lg:self-start">
          <ProgramSummaryRail
            client={
              selectedClient?.kind === "new"
                ? {
                    name: `${selectedClient.firstName} ${selectedClient.lastName}`,
                    invitation: {
                      firstName: selectedClient.firstName,
                      email: selectedClient.email,
                    },
                  }
                : selectedClient
            }
            startDate={values.startDate}
            endDate={values.endDate}
            cadenceSummary={cadenceSummary}
            sessionCount={sessionCount}
            invoiceTotal={invoiceTotal}
            invoiceCount={invoiceCount}
            submitDisabled={submitDisabled}
            isSubmitting={isSubmitting}
            disabledReason={disabledReason}
            onSubmit={handleSubmit}
            onSaveAsTemplate={saveAsTemplate}
          />
        </aside>
      </div>

      <AddClientDialog
        open={addClientInitial !== null}
        onOpenChange={(open) => {
          if (!open) setAddClientInitial(null);
        }}
        initialValues={addClientInitial ?? {}}
        ownEmails={ownEmails}
        onAdd={(client) => {
          form.setFieldValue("selectedClient", { kind: "new", ...client });
          setSearchValue("");
          setAddClientInitial(null);
        }}
      />

      <ProgramTemplatesDialog
        open={templateDialog !== null}
        onOpenChange={(open) => {
          if (!open) setTemplateDialog(null);
        }}
        newTemplate={templateDialog?.newTemplate}
      />
    </div>
  );
}

/** Inline calendar-popover date picker bound to a TanStack Form field. */
function DateField({
  label,
  value,
  placeholder,
  onSelect,
  isDisabled,
}: {
  label: string;
  value?: Date;
  placeholder: string;
  onSelect: (date: Date) => void;
  isDisabled: (date: Date) => boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div className="relative" suppressHydrationWarning>
            <CalendarIcon className="absolute top-1/2 left-4 size-4 -translate-y-1/2" />
            <Input
              readOnly
              active={open}
              value={value ? formatDateOnly(value) : ""}
              placeholder={placeholder}
              className="h-10 w-full cursor-pointer pl-10"
            />
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            defaultMonth={value}
            selected={value}
            onSelect={(date) => {
              if (date) {
                onSelect(date);
                setOpen(false);
              }
            }}
            disabled={isDisabled}
            className="w-72"
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
